import type { LearnContext, LearnTopic } from '../types';
import { $, pct } from '../format';

/** Which short strike is nearer, as a percent of its own strike — the Risk view's ranking. */
function nearerSide(ctx: LearnContext, putPercent: number, callPercent: number): 'put' | 'call' {
  if (ctx.safety?.closestSide) return ctx.safety.closestSide;
  return putPercent <= callPercent ? 'put' : 'call';
}

export const atRiskTopic: LearnTopic = {
  id: 'at-risk',
  tier: 'position',
  title: 'Which side is at risk right now',
  summary: 'The nearer short strike is the one to watch. A breach means the stock has moved through it.',
  body: [
    'An iron condor only loses if the stock moves through one of the short strikes. Distance to each short strike, as a percentage of the strike, is the fastest read of how safe each side is.',
    'The nearer side is the one to watch. When the stock is closer to the short put than the short call, a further drop is the risk; when it is closer to the short call, a rally is. Steward colours each side by that distance: green with room, amber when it is getting close, red when it is within a couple of percent or through.',
    'Breached means the stock has moved through the short strike: that spread is now in the money — the stock is past the strike, so the option has real value — and losing value, capped by its wing. A breach is not a loss until expiration or until you close, but it is the moment to decide whether to roll, close, or hold.',
  ],
  explain(ctx) {
    const { symbol, price, shortPut, shortCall } = ctx;
    if (shortPut == null && shortCall == null) {
      return ['This position has no short strikes to watch.'];
    }
    if (price == null) {
      return [
        `There is no live price for ${symbol} right now, so the distance to each short strike cannot be measured.`,
      ];
    }

    const sentences: string[] = [];
    const putDistance = shortPut != null ? price - shortPut : null;
    const callDistance = shortCall != null ? shortCall - price : null;

    if (shortPut != null && putDistance != null) {
      sentences.push(
        putDistance < 0
          ? `The put side is breached: ${symbol} is at ${$(price)}, below your short ${shortPut} put by ${$(-putDistance)}.`
          : `Your short put is ${shortPut}; ${symbol} at ${$(price)} is ${$(putDistance)} above it, ${pct((putDistance / shortPut) * 100)} of room.`,
      );
    }
    if (shortCall != null && callDistance != null) {
      sentences.push(
        callDistance < 0
          ? `The call side is breached: ${symbol} is at ${$(price)}, above your short ${shortCall} call by ${$(-callDistance)}.`
          : `Your short call is ${shortCall}; ${symbol} at ${$(price)} is ${$(callDistance)} below it, ${pct((callDistance / shortCall) * 100)} of room.`,
      );
    }

    if (shortPut != null && shortCall != null) {
      if (putDistance != null && callDistance != null && putDistance >= 0 && callDistance >= 0) {
        const nearer = nearerSide(ctx, (putDistance / shortPut) * 100, (callDistance / shortCall) * 100);
        sentences.push(
          `The ${nearer} side is nearer, so a ${nearer === 'put' ? 'drop' : 'rally'} is the move that would hurt first.`,
        );
      }
    } else {
      sentences.push(`This position has only a ${shortPut != null ? 'put' : 'call'} side to watch.`);
    }

    return sentences;
  },
  appliesTo(ctx) {
    return ctx.shortPut != null || ctx.shortCall != null;
  },
};
